import React, { useState, useEffect, useRef } from 'react';
import { Typography, Box, Alert, useTheme, useMediaQuery } from '@mui/material';
import { useLocation } from 'react-router-dom';
import CRTEffect from './CRTEffect';
import UnifiedSidebar from './UnifiedSidebar';

interface LayoutProps {
  children: React.ReactNode;
  themeMode: 'light' | 'dark' | 'retro';
  onToggleTheme: (ctrlKey?: boolean) => void;
}

export default function Layout({ children, themeMode, onToggleTheme }: LayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(true); // Default open on desktop
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const location = useLocation();
  const isTabletOrDown = useMediaQuery(theme.breakpoints.down('md'));
  const contentPaddingX = isTabletOrDown ? 1.5 : 2;
  const safeAreaPadding = 'env(safe-area-inset-bottom, 0px)';

  const isFullWidthPage =
    location.pathname === '/' ||
    location.pathname === '/chat' ||
    location.pathname.startsWith('/forms');

  // studio manages its own scrolling, padding, and layout — give it the full area
  const isFullBleedPage =
    location.pathname.startsWith('/studio');
  
  const wasDesktop = useRef(isDesktop);

  useEffect(() => {
    if (wasDesktop.current && !isDesktop) {
      setSidebarOpen(false);
    } else if (!wasDesktop.current && isDesktop) {
      setSidebarOpen(true);
    }
    wasDesktop.current = isDesktop;
  }, [isDesktop]);

  return (
    <CRTEffect>
      <Box sx={{ display: 'flex', height: '100vh' }}>
        <UnifiedSidebar
          open={sidebarOpen}
          collapsed={sidebarCollapsed}
          onToggle={() => setSidebarOpen(!sidebarOpen)}
          onCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
          onClose={() => setSidebarOpen(false)}
          variant={isDesktop ? 'persistent' : 'temporary'}
          themeMode={themeMode}
          onToggleTheme={onToggleTheme}
        />
        
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            transition: 'width 0.3s ease',
            overflow: 'hidden',
            minHeight: 0,
            width: '100%',
          }}
        >
          {/* Environment Alerts */}
          {(process.env.REACT_APP_ENVIRONMENT === 'dev' || process.env.REACT_APP_ENVIRONMENT === 'testing') && (
            <Alert severity="info" sx={{ borderRadius: 0, flexShrink: 0, backgroundColor: process.env.REACT_APP_ENVIRONMENT === 'dev' ? '#1976d2' : '#ed6c02', color: 'white' }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {process.env.REACT_APP_ENVIRONMENT?.toUpperCase()} ENVIRONMENT
              </Typography>
            </Alert>
          )}

          {/* Main Content Area */}
          <Box
            sx={{
              flex: 1,
              overflow: isFullBleedPage ? 'hidden' : 'auto',
              ...(isFullBleedPage
                ? {}
                : {
                    py: 3,
                    px: contentPaddingX,
                    pb: `calc(${theme.spacing(4)} + ${safeAreaPadding})`,
                  }),
            }}
          >
            {isFullBleedPage ? (
              children
            ) : isFullWidthPage ? (
              <Box>
                {children}
              </Box>
            ) : (
              <Box sx={{ maxWidth: '1200px', mx: 'auto' }}>
                {children}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </CRTEffect>
  );
}